import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { createLossReason, updateLossReason } from "../../services/lossReasons";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const LossReasonSchema = Yup.object().shape({
    name: Yup.string()
        .min(3, "Nome muito curto")
        .required("Obrigatório"),
});

const LossReasonModal = ({ open, onClose, lossReason, reload }) => {
    const classes = useStyles();

    const initialState = {
        name: "",
        description: "",
    };

    const [data, setData] = useState(initialState);

    useEffect(() => {
        if (lossReason) {
            setData({
                name: lossReason.name || "",
                description: lossReason.description || "",
            });
        } else {
            setData(initialState);
        }
    }, [lossReason, open]);

    const handleClose = () => {
        setData(initialState);
        onClose();
    };

    const handleSave = async (values) => {
        try {
            if (lossReason) {
                await updateLossReason(lossReason.id, values);
            } else {
                await createLossReason(values);
            }
            toast.success(i18n.t("kanban.lossReasons.toasts.success"));
            if (reload) reload();
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {lossReason
                        ? i18n.t("kanban.lossReasons.modal.edit")
                        : i18n.t("kanban.lossReasons.modal.add")}
                </DialogTitle>
                <Formik
                    initialValues={data}
                    enableReinitialize={true}
                    validationSchema={LossReasonSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        setTimeout(() => {
                            handleSave(values);
                            setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting }) => (
                        <Form>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.lossReasons.modal.form.name")}
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.lossReasons.modal.form.description")}
                                    name="description"
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {lossReason
                                        ? i18n.t("global.buttons.save")
                                        : i18n.t("global.buttons.add")}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default LossReasonModal;
